import { useState, useEffect } from 'react'
import { X, UserPlus, Bell, Warning, MagnifyingGlass, Envelope } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'

export default function InviteMemberModal({ isOpen, onClose, subgroupId, orgId, onSuccess }) {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [checking, setChecking] = useState(false)
  const [userStatus, setUserStatus] = useState(null)
  const [inviteSent, setInviteSent] = useState(false)
  const [existingUserData, setExistingUserData] = useState(null)
  const [pendingInviteWarning, setPendingInviteWarning] = useState(null)

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required'
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address'
    }
    return null
  }

  const handleClose = () => {
    setEmail('')
    setEmailError('')
    setName('')
    setPhone('')
    setUserStatus(null)
    setExistingUserData(null)
    setInviteSent(false)
    setPendingInviteWarning(null)
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setEmailError('')
      setName('')
      setPhone('')
      setUserStatus(null)
      setExistingUserData(null)
      setInviteSent(false)
      setPendingInviteWarning(null)
    }
  }, [isOpen])

  useEffect(() => {
    const checkPendingInvite = async () => {
      if (!email.trim()) {
        setPendingInviteWarning(null)
        return
      }

      const error = validateEmail(email)
      if (error) {
        setPendingInviteWarning(null)
        return
      }

      try {
        const [existingInvite, existingEmailInvite] = await Promise.all([
          supabase
            .from('invitations')
            .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
            .eq('invitee_email', email.toLowerCase().trim())
            .eq('status', 'pending')
            .maybeSingle(),
          supabase
            .from('email_invites')
            .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
            .eq('email', email.toLowerCase().trim())
            .maybeSingle()
        ])

        if (existingInvite.data) {
          const inviteDate = new Date(existingInvite.data.created_at).toLocaleDateString()
          const departmentName = existingInvite.data.groups?.name || 'a department'
          setPendingInviteWarning(
            `This email already has a pending invitation to ${departmentName} (sent on ${inviteDate}). ` +
            `They must accept or decline that invitation before receiving another one.`
          )
        } else if (existingEmailInvite.data) {
          const inviteDate = new Date(existingEmailInvite.data.created_at).toLocaleDateString()
          const departmentName = existingEmailInvite.data.groups?.name || 'a department'
          setPendingInviteWarning(
            `An invitation email was already sent to this address for ${departmentName} on ${inviteDate}. ` +
            `Please wait for them to complete signup before sending another invitation.`
          )
        } else {
          setPendingInviteWarning(null)
        }
      } catch (error) {
        console.error('Error checking pending invites:', error)
      }
    }

    checkPendingInvite()
  }, [email])

  const handleEmailChange = (e) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    if (emailError) {
      setEmailError('')
    }
  }

  const handleEmailBlur = () => {
    const error = validateEmail(email)
    setEmailError(error || '')
  }

  const checkUserStatus = async () => {
    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }

    setChecking(true)
    setUserStatus(null)
    setExistingUserData(null)
    setEmailError('')

    try {
      const [existingInvite, existingEmailInvite] = await Promise.all([
        supabase
          .from('invitations')
          .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
          .eq('invitee_email', email.toLowerCase().trim())
          .eq('status', 'pending')
          .maybeSingle(),
        supabase
          .from('email_invites')
          .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle()
      ])

      if (existingInvite.data) {
        const inviteDate = new Date(existingInvite.data.created_at).toLocaleDateString()
        const departmentName = existingInvite.data.groups?.name || 'a department'
        setPendingInviteWarning(
          `This email already has a pending invitation to ${departmentName} (sent on ${inviteDate}). ` +
          `They must accept or decline that invitation before receiving another one.`
        )
        setChecking(false)
        return
      }

      if (existingEmailInvite.data) {
        const inviteDate = new Date(existingEmailInvite.data.created_at).toLocaleDateString()
        const departmentName = existingEmailInvite.data.groups?.name || 'a department'
        setPendingInviteWarning(
          `An invitation email was already sent to this address for ${departmentName} on ${inviteDate}. ` +
          `Please wait for them to complete signup before sending another invitation.`
        )
        setChecking(false)
        return
      }

      const { data: users } = await supabase
        .from('users')
        .select('id, name, email, phone, role, leadership_level')
        .eq('email', email.toLowerCase().trim())
        .eq('org_id', orgId)
        .maybeSingle()

      if (users) {
        const { data: memberships } = await supabase
          .from('group_members')
          .select(`
            group_id,
            groups!inner (
              id,
              name,
              depth
            )
          `)
          .eq('user_id', users.id)
          .eq('groups.depth', 3)

        if (memberships && memberships.length > 0) {
          setUserStatus('assigned')
          setExistingUserData({
            ...users,
            currentSubgroup: memberships[0].groups.name
          })
        } else {
          setUserStatus('unassigned')
          setExistingUserData(users)
        }
      } else {
        setUserStatus('not_exists')
      }
    } catch (error) {
      console.error('Error checking user:', error)
      alert('Failed to check user status')
    } finally {
      setChecking(false)
    }
  }

  const sendInvitation = async () => {
    if (!existingUserData) return

    setSubmitting(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { error: inviteError } = await supabase
        .from('invitations')
        .insert({
          inviter_id: user.id,
          invitee_email: existingUserData.email,
          invitee_id: existingUserData.id,
          subgroup_id: subgroupId,
          status: 'pending'
        })

      if (inviteError) {
        if (inviteError.code === '23505') {
          alert('This person already has a pending invitation to this subgroup.')
          handleClose()
          return
        }
        throw inviteError
      }

      setInviteSent(true)
      
      if (onSuccess) onSuccess()

    } catch (error) {
      console.error('Error sending invitation:', error)
      alert('Failed to send invitation: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const sendInviteEmail = async () => {
    if (!name.trim()) {
      alert('Please enter the person\'s name')
      return
    }

    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }

    setSubmitting(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const [existingInvite, existingEmailInvite] = await Promise.all([
        supabase
          .from('invitations')
          .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
          .eq('invitee_email', email.toLowerCase().trim())
          .eq('status', 'pending')
          .maybeSingle(),
        supabase
          .from('email_invites')
          .select('id, created_at, subgroup_id, groups!subgroup_id(name)')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle()
      ])

      if (existingInvite.data) {
        const inviteDate = new Date(existingInvite.data.created_at).toLocaleDateString()
        const departmentName = existingInvite.data.groups?.name || 'a department'
        setPendingInviteWarning(
          `This email already has a pending invitation to ${departmentName} (sent on ${inviteDate}). ` +
          `They must accept or decline that invitation before receiving another one.`
        )
        setSubmitting(false)
        return
      }

      if (existingEmailInvite.data) {
        const inviteDate = new Date(existingEmailInvite.data.created_at).toLocaleDateString()
        const departmentName = existingEmailInvite.data.groups?.name || 'a department'
        setPendingInviteWarning(
          `An invitation email was already sent to this address for ${departmentName} on ${inviteDate}. ` +
          `Please wait for them to complete signup before sending another invitation.`
        )
        setSubmitting(false)
        return
      }

      const { error: trackingError } = await supabase
        .from('email_invites')
        .insert({
          inviter_id: user.id,
          email: email.toLowerCase().trim(),
          subgroup_id: subgroupId
        })

      if (trackingError) {
        console.error('Error inserting into email_invites:', trackingError)
        if (trackingError.code === '23505') {
          setPendingInviteWarning('An invitation was already sent to this email address for this department.')
          setSubmitting(false)
          return
        }
        alert(`Failed to create invitation tracking: ${trackingError.message}`)
        throw trackingError
      }

      const { data: subgroupData } = await supabase
        .from('groups')
        .select('name, org_id')
        .eq('id', subgroupId)
        .single()

      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', subgroupData.org_id)
        .single()

      const signupLink = `${window.location.origin}/signup?email=${encodeURIComponent(email.trim())}&name=${encodeURIComponent(name.trim())}&phone=${encodeURIComponent(phone.trim() || '')}&org=${subgroupData.org_id}&invite_subgroup=${subgroupId}&type=invite`

      const { error: emailError } = await supabase.functions.invoke('send-invite-email', {
        body: {
          recipientEmail: email.trim(),
          recipientName: name.trim(),
          signupLink: signupLink,
          orgName: orgData.name,
          subgroupName: subgroupData.name,
          memberType: 'invite'
        }
      })

      if (emailError) {
        throw new Error(`Failed to send invitation email: ${emailError.message}`)
      }

      setInviteSent(true)
      
      if (onSuccess) onSuccess()

    } catch (error) {
      console.error('Error sending email:', error)
      alert('Failed to send invitation email: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-xl">
              <UserPlus size={24} weight="light" className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Invite Member</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={24} weight="light" />
          </button>
        </div>

        <div className="p-4">
          {inviteSent ? (
            <div className="text-center py-8">
              <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Bell size={32} weight="light" className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {userStatus === 'not_exists' ? 'Invitation Sent!' : 'Notification Sent!'}
              </h3>
              <p className="text-gray-600 mb-6">
                {userStatus === 'not_exists' 
                  ? `An invitation email has been sent to ${email}`
                  : `${existingUserData?.name} has been notified of the invitation`
                }
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  placeholder="member@example.com"
                  className={`w-full px-4 py-3 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {emailError && (
                  <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                    <Warning size={16} weight="fill" />
                    <span>{emailError}</span>
                  </div>
                )}
                {!emailError && !pendingInviteWarning && (
                  <p className="text-xs text-gray-500 mt-2">
                    We'll check if this person is already in your organization
                  </p>
                )}
              </div>

              {pendingInviteWarning && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">{pendingInviteWarning}</p>
                  </div>
                </div>
              )}

              {!userStatus && (
                <button
                  onClick={checkUserStatus}
                  disabled={checking || !email.trim() || !!emailError}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {checking ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Checking...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlass size={20} weight="light" />
                      Check User Status
                    </>
                  )}
                </button>
              )}

              {userStatus === 'assigned' && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>{existingUserData.name}</strong> is already in your organization
                  </p>
                  <p className="text-sm text-gray-600">
                    Currently assigned to: <strong>{existingUserData.currentSubgroup}</strong>
                  </p>
                  <button
                    onClick={() => {
                      setUserStatus(null)
                      setExistingUserData(null)
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Check different email
                  </button>
                </div>
              )}

              {userStatus === 'unassigned' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>{existingUserData.name}</strong> is in your organization but not assigned to any department. They will receive a notification to join.
                    </p>
                  </div>

                  <button
                    onClick={sendInvitation}
                    disabled={submitting}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Bell size={20} weight="light" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </>
              )}

              {userStatus === 'not_exists' && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-gray-600">
                      This person is not in your organization yet. Please provide their details.
                    </p>
                  </div>

                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Will appear in the invitation"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Will pre-fill signup form"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>{name || 'They'}</strong> will receive an email with a signup link. When they complete registration, they'll be automatically assigned to this department.
                    </p>
                  </div>

                  <button
                    onClick={sendInviteEmail}
                    disabled={submitting || !name.trim()}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending Email...
                      </>
                    ) : (
                      <>
                        <Envelope size={20} weight="light" />
                        Send Invitation Email
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}